import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    AspectRatio,
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Button,
    Card,
    CardBody,
    Center,
    Flex,
    Grid,
    GridItem,
    HStack,
    Heading,
    Highlight,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Link,
    Stack,
    Tag,
    TagCloseButton,
    Text,
    VStack,
    useColorModeValue,
} from "@chakra-ui/react";
import i18next from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    RiArrowLeftDoubleLine,
    RiArrowLeftLine,
    RiArrowRightDoubleLine,
    RiArrowRightLine,
    RiCircleFill,
    RiCloseLine,
    RiDeleteBinLine,
    RiGameFill,
    RiGamepadFill,
} from "react-icons/ri";
import {
    Link as RouterLink,
    useLoaderData,
    useSearchParams,
    type LoaderFunctionArgs,
} from "react-router";
import { getApiV1Games, getApiV1Tags, type GameItem, type GameTagItem } from "../../client";
import { GALLERY_PAGE_SIZE, MEDIA_ASPECT } from "../../constants";
import { apiData, apiDataWithHeaders } from "../../utils/api";
import { getPrefered } from "../../utils/preferedLanguage";
import { sequence } from "../../utils/sequence";
import { PivoImage } from "../media/PivoImage";
import { OutletOrChildren } from "../utils/OutletOrChildren";
import { PageTitle } from "../utils/PageTitle";

/** Delay before a typed search is committed to the URL (and re-fetched). */
const SEARCH_DEBOUNCE_MS = 300;

const SORT_VALUES: Record<string, string> = {
    recent: "-latestReleasedOn",
    oldest: "latestReleasedOn",
    az: "name",
    za: "-name",
};

export { RouteErrorBoundary as ErrorBoundary } from "../utils/RouteErrorBoundary";

// Fetched on the server during SSR (and on the server again for client-side
// navigations). Only the exact `/games` page needs gallery data; child routes
// (`/games/:gameId/...`) render the outlet instead.
export async function loader({ request, params }: LoaderFunctionArgs) {
    if (params.gameId) {
        return null;
    }

    const url = new URL(request.url);
    const page = Math.max(parseInt(url.searchParams.get("page") ?? "") || 0, 0);
    const search = url.searchParams.get("search") ?? "";
    const filter = (url.searchParams.get("filter") ?? "").split(",").filter((tag) => tag.length > 0);
    const sort = url.searchParams.get("sort") ?? "recent";

    const [tags, games] = await Promise.all([
        apiData(getApiV1Tags({ query: { pageSize: -1 } })),
        apiDataWithHeaders(
            getApiV1Games({
                query: {
                    pageSize: GALLERY_PAGE_SIZE,
                    pageIndex: page,
                    sort: SORT_VALUES[sort] ?? SORT_VALUES.recent,
                    lang: i18next.language,
                    name: search.length > 0 ? search : undefined,
                    tag: filter.length > 0 ? filter : undefined,
                },
            }),
            ["x-total-count"],
        ),
    ]);

    return { tags, games: games.data, total: parseInt(games.headers["x-total-count"]) };
}

/** Shared selectable tag appearance (sort selector and filter accordion). */
function SelectableTag({
    label,
    selected = false,
    onClick,
}: {
    label: string;
    selected?: boolean;
    onClick?: () => void;
}) {
    const selectedBg = useColorModeValue("brand.500", "brand.400");
    const inactiveBorderColor = useColorModeValue("gray.300", "gray.700");
    const hoverBg = useColorModeValue("gray.200", "gray.600");

    return (
        <Tag
            size="md"
            fontSize="xs"
            variant={selected ? "solid" : "transparent"}
            border={selected ? "0px" : "1px"}
            backgroundColor={selected ? selectedBg : "transparent"}
            borderColor={selected ? "transparent" : inactiveBorderColor}
            cursor="pointer"
            _hover={{
                backgroundColor: selected ? undefined : hoverBg,
            }}
            onClick={onClick}
        >
            {label}
        </Tag>
    );
}

function ActiveFilterTag({ title, onRemove }: { title: string; onRemove: () => void }) {
    return (
        <Tag
            size="md"
            fontSize="xs"
            variant="solid"
            backgroundColor={useColorModeValue("brand.500", "brand.400")}
            color="white"
        >
            {title}
            <TagCloseButton onClick={onRemove} />
        </Tag>
    );
}

function TagGroupSection({
    tagGroup,
    tags,
    excludedFilter,
    onAddTag,
}: {
    tagGroup: GameTagItem;
    tags: GameTagItem[];
    excludedFilter: Set<string>;
    onAddTag: (tag: string) => void;
}) {
    return (
        <Flex direction="row" paddingTop={3} columnGap={14} rowGap={5} wrap={"wrap"}>
            <Box>
                <Heading
                    fontFamily={"body"}
                    fontSize="sm"
                    fontWeight={"semibold"}
                    color={useColorModeValue("gray.900", "gray.50")}
                >
                    {getPrefered(tagGroup.name)}
                </Heading>
                <HStack spacing={2} paddingTop={2} wrap={"wrap"}>
                    {tags
                        .filter((tag) => tag.parentId === tagGroup.id && !excludedFilter.has(tag.id))
                        .map((tag) => (
                            <SelectableTag
                                key={tag.id}
                                label={getPrefered(tag.name)}
                                onClick={() => onAddTag(tag.id)}
                            />
                        ))}
                </HStack>
            </Box>
        </Flex>
    );
}

function GameTagChip({
    details,
    selected,
    onToggle,
}: {
    details: GameTagItem;
    selected: boolean;
    onToggle: (tag: string) => void;
}) {
    const title = getPrefered(details.name);
    const borderColor = useColorModeValue("gray.300", "gray.700");
    const hoverBg = useColorModeValue("gray.200", "gray.600");

    return (
        <Tag
            key={details.id ?? title}
            size="md"
            fontSize="xs"
            variant={selected ? "solid" : "transparent"}
            border={"1px"}
            backgroundColor={selected ? "brand.400" : "transparent"}
            borderColor={borderColor}
            mr={2}
            mb={2}
            _hover={{
                backgroundColor: selected ? undefined : hoverBg,
            }}
            onClick={(event) => {
                onToggle(details.id);
                event.preventDefault();
                event.stopPropagation();
            }}
        >
            <Text fontSize="xs">{title}</Text>
        </Tag>
    );
}

function GameCard({
    game,
    tags,
    filter,
    search,
    onToggleTag,
}: {
    game: GameItem;
    tags: GameTagItem[];
    filter: Set<string>;
    search: string;
    onToggleTag: (tag: string) => void;
}) {
    return (
        <GridItem key={game.id}>
            <Link as={RouterLink} to={game.id + (game.latestReleaseId ? "/releases/" + game.latestReleaseId : "")}>
                <Card variant="unstyled" backgroundColor="transparent" flexGrow={1} flexShrink={0}>
                    <CardBody>
                        <AspectRatio
                            ratio={MEDIA_ASPECT}
                            w="100%"
                            borderRadius="md"
                            sx={{
                                boxShadow: useColorModeValue(
                                    "2px 2px 10px rgba(128, 128, 128, 0.7)",
                                    "2px 2px 10px rgba(44, 48, 68, 0.7)",
                                ),
                            }}
                            background={useColorModeValue("gray.300", "gray.700")}
                            overflow="hidden"
                        >
                            {game.coverImageId ? (
                                <PivoImage
                                    id={game.coverImageId}
                                    thumbHash={game.coverImageThumbHash}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                    sizes="(max-width: 768px) 15rem, 20rem"
                                    imageProps={{
                                        alt: "",
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            ) : (
                                <Center>
                                    <RiGameFill size={40} />
                                    <RiCircleFill size={10} />
                                    <span style={{ width: 5 }} />
                                    <RiCircleFill size={10} />
                                    <span style={{ width: 5 }} />
                                    <RiCircleFill size={10} />
                                </Center>
                            )}
                        </AspectRatio>

                        <Stack mt="4" spacing="1">
                            <Heading size="md">
                                <Highlight
                                    query={search}
                                    styles={{
                                        bg: "brand.400",
                                        color: "white",
                                    }}
                                >
                                    {getPrefered(game.name)}
                                </Highlight>
                            </Heading>
                            {game.tags && (
                                <Box>
                                    {(game.tags as string[]).map((tag) => {
                                        const tagDetails = tags.find((t) => t.id === tag);
                                        if (!tagDetails) {
                                            return null;
                                        }
                                        return (
                                            <GameTagChip
                                                key={tagDetails.id ?? getPrefered(tagDetails.name)}
                                                details={tagDetails}
                                                selected={filter.has(tagDetails.id)}
                                                onToggle={onToggleTag}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        </Stack>
                    </CardBody>
                </Card>
            </Link>
        </GridItem>
    );
}

export function GamesGallery() {
    const { t } = useTranslation();
    const data = useLoaderData<typeof loader>();
    const [searchParams, setSearchParams] = useSearchParams();
    const breadcrumbColor = useColorModeValue("black", "white");
    const headingColor = useColorModeValue("gray.900", "gray.50");
    const placeholderColor = useColorModeValue("gray.400", "gray.600");
    const inputBorderColor = useColorModeValue("gray.300", "gray.700");

    const page = Math.max(parseInt(searchParams.get("page") ?? "") || 0, 0);
    const search = searchParams.get("search") ?? "";

    // NB: The input is implemented so that it stays responsive, even though API requests are sent and
    //     URL search params are updated.

    // The actual value of the <input /> element
    const [searchInput, setSearchInput] = useState(search);

    // Last value committed to the URL
    const committedSearch = useRef(search);

    // Dependency-free reference to current searchParams.
    // Allows us to modify current searchParams even if they change within the debounce timeout.
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;

    useEffect(() => {
        // called when the ?search param changes

        if (search !== committedSearch.current) {
            committedSearch.current = search;
            setSearchInput(search);
        }
    }, [search]);

    useEffect(() => {
        // called when the value of <input /> changes

        if (searchInput === committedSearch.current) {
            return;
        }

        const timeout = setTimeout(() => {
            committedSearch.current = searchInput;
            const next = new URLSearchParams(searchParamsRef.current);
            if (searchInput.length > 0) {
                next.set("search", searchInput);
            } else {
                next.delete("search");
            }

            next.set("page", "0");
            setSearchParams(next);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
        // NB: No, we don't want setSearchParams as dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const filter = new Set<string>((searchParams.get("filter") ?? "").split(",").filter((tag) => tag.length > 0));
    const selectedSort = searchParams.get("sort") ?? "recent";

    const sortBy: Record<string, { label: string; value: string }> = {
        recent: { label: t("games.recent"), value: SORT_VALUES.recent },
        oldest: { label: t("games.oldest"), value: SORT_VALUES.oldest },
        az: { label: t("games.az"), value: SORT_VALUES.az },
        za: { label: t("games.za"), value: SORT_VALUES.za },
    };

    const updateSearchParams = (update: (params: URLSearchParams) => void) => {
        const next = new URLSearchParams(searchParams);
        update(next);
        setSearchParams(next);
    };

    const setPage = (page: number) => updateSearchParams((params) => params.set("page", String(page)));

    const clearSearch = () =>
        updateSearchParams((params) => {
            params.delete("search");
            params.set("page", "0");
        });

    const setSorting = (sort: keyof typeof sortBy) =>
        updateSearchParams((params) => {
            params.set("sort", sort);
            params.set("page", "0");
        });

    const setFilterSet = (nextFilter: Set<string>) =>
        updateSearchParams((params) => {
            if (nextFilter.size > 0) {
                params.set("filter", [...nextFilter.values()].join(","));
            } else {
                params.delete("filter");
            }
            params.set("page", "0");
        });

    const addTag = (tag: string) => setFilterSet(new Set(filter).add(tag));

    const removeTag = (tag: string) => {
        const next = new Set(filter);
        next.delete(tag);
        setFilterSet(next);
    };

    const toggleTag = (tag: string) => {
        const next = new Set(filter);
        if (!next.delete(tag)) {
            next.add(tag);
        }
        setFilterSet(next);
    };

    const clearTags = () => setFilterSet(new Set());

    const tags = data?.tags ?? [];
    const gameItems = data?.games ?? [];
    // WIP item: the gallery reserves one slot for the "work in progress" card
    const total = (data?.total ?? 0) + 1;
    const pages = Math.ceil(total / GALLERY_PAGE_SIZE);

    return (
        <OutletOrChildren>
            {data && (
                <Box>
                    <Box position="relative" w="100%">
                        <Breadcrumb position="relative" top={7} left={10} color={breadcrumbColor}>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/">Garrigue Games</BreadcrumbLink>
                            </BreadcrumbItem>

                            <BreadcrumbItem fontWeight={"semibold"} isCurrentPage>
                                <BreadcrumbLink href="/games">{t("menuButtons.games")}</BreadcrumbLink>
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </Box>

                    <VStack maxW={1536} p={10} paddingTop={16} alignItems={"left"}>
                        <PageTitle>{t("games.title")}</PageTitle>

                        <Heading fontFamily={"body"} fontSize="md" fontWeight={"semibold"} color={headingColor}>
                            {t("games.search")}
                        </Heading>

                        <InputGroup>
                            <Input
                                placeholder={t("games.searchfor")}
                                _placeholder={{ opacity: 1, color: placeholderColor }}
                                borderColor={inputBorderColor}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                focusBorderColor="brand.500"
                            />
                            {searchInput.length > 0 && (
                                <InputRightElement>
                                    <IconButton
                                        aria-label="Clear search"
                                        icon={<RiCloseLine />}
                                        variant="ghost"
                                        color="brand.500"
                                        onClick={() => {
                                            // Clearing is a deliberate action —
                                            // skip the debounce.
                                            committedSearch.current = "";
                                            setSearchInput("");
                                            clearSearch();
                                        }}
                                    />
                                </InputRightElement>
                            )}
                        </InputGroup>

                        {filter.size > 0 && (
                            <HStack spacing={2} paddingTop={3} wrap={"wrap"} mb={2}>
                                {[...filter.values()].map((tagId) => {
                                    const tag = tags.find((t) => t.id === tagId);
                                    if (!tag) {
                                        return null;
                                    }

                                    return (
                                        <ActiveFilterTag
                                            key={tag.id}
                                            title={getPrefered(tag.name)}
                                            onRemove={() => removeTag(tagId)}
                                        />
                                    );
                                })}
                                {filter.size > 0 && (
                                    <Tag
                                        size="md"
                                        fontSize="xs"
                                        variant="solid"
                                        cursor="pointer"
                                        onClick={clearTags}
                                        bgColor="white"
                                        color="blackAlpha.800"
                                    >
                                        <RiDeleteBinLine />
                                    </Tag>
                                )}
                            </HStack>
                        )}

                        <Accordion allowMultiple width="100%" mt={2}>
                            <AccordionItem border="none">
                                <AccordionButton padding={0} _hover={{ bg: "transparent" }}>
                                    <Heading
                                        fontFamily={"body"}
                                        fontSize="md"
                                        fontWeight={"semibold"}
                                        color={headingColor}
                                    >
                                        {t("games.filtering")}
                                    </Heading>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel padding={0} pb={4}>
                                    {tags
                                        .filter((tag) => tag.parentId === null)
                                        .map((tagGroup) => (
                                            <TagGroupSection
                                                key={tagGroup.id ?? getPrefered(tagGroup.name)}
                                                tagGroup={tagGroup}
                                                tags={tags}
                                                excludedFilter={filter}
                                                onAddTag={addTag}
                                            />
                                        ))}
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>

                        <Flex direction="row" paddingTop={3} columnGap={14} rowGap={5} wrap={"wrap"}>
                            <Box>
                                <Heading fontFamily={"body"} fontSize="md" fontWeight={"semibold"} color={headingColor}>
                                    {t("games.sort")}
                                </Heading>
                                <HStack spacing={2} paddingTop={2} wrap={"wrap"}>
                                    {Object.entries(sortBy).map(([key, tag]) => (
                                        <SelectableTag
                                            key={key}
                                            label={tag.label}
                                            selected={selectedSort === key}
                                            onClick={() => setSorting(key)}
                                        />
                                    ))}
                                </HStack>
                            </Box>
                        </Flex>

                        <Box position="relative">
                            <Grid
                                paddingTop={5}
                                columnGap={6}
                                rowGap={8}
                                templateColumns={{
                                    base: "1fr",
                                    md: "repeat(2, minmax(0, 1fr))",
                                    lg: "repeat(3, minmax(0, 1fr))",
                                    xl: "repeat(4, minmax(0, 1fr))",
                                }}
                            >
                                {gameItems.map((game) => (
                                    <GameCard
                                        key={game.id}
                                        game={game}
                                        tags={tags}
                                        filter={filter}
                                        search={search}
                                        onToggleTag={toggleTag}
                                    />
                                ))}

                                {page === pages - 1 && (
                                    <Card maxW="xs" variant="unstyled" backgroundColor="transparent">
                                        <CardBody>
                                            <Center h="100%" overflow="hidden" opacity={0.2}>
                                                <VStack textAlign="center" px={4}>
                                                    <Icon as={RiGamepadFill} boxSize={20} />
                                                    <Text fontWeight="bold">{t("games.wip1")}</Text>
                                                    <Text>{t("games.wip2")}</Text>
                                                    <Text pt={4}>{t("games.wip3")}</Text>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        maxWidth={120}
                                                        backgroundColor={"transparent"}
                                                        borderColor={inputBorderColor}
                                                    >
                                                        <Link
                                                            onClick={(e) => {
                                                                window.location.href = "mailto:kafe@fi.muni.cz";
                                                                e.preventDefault();
                                                            }}
                                                        >
                                                            {t("about.paragraph6")}
                                                        </Link>
                                                    </Button>
                                                </VStack>
                                            </Center>
                                        </CardBody>
                                    </Card>
                                )}
                            </Grid>
                            <HStack mt={8} mb={6}>
                                <IconButton
                                    isDisabled={page === 0}
                                    onClick={() => setPage(0)}
                                    aria-label={`first page`}
                                    icon={<RiArrowLeftDoubleLine />}
                                />
                                <IconButton
                                    isDisabled={page === 0}
                                    onClick={() => setPage(page - 1)}
                                    aria-label={`previous page`}
                                    icon={<RiArrowLeftLine />}
                                    mr={4}
                                />
                                {sequence(pages).map((pageId) => (
                                    <IconButton
                                        key={pageId}
                                        isActive={page === pageId}
                                        onClick={() => setPage(pageId)}
                                        aria-label={`page ${pageId + 1}`}
                                        icon={<Text>{pageId + 1}</Text>}
                                    />
                                ))}
                                <IconButton
                                    isDisabled={page === pages - 1}
                                    onClick={() => setPage(page + 1)}
                                    aria-label={`next page`}
                                    icon={<RiArrowRightLine />}
                                    ml={4}
                                />
                                <IconButton
                                    isDisabled={page === pages - 1}
                                    onClick={() => setPage(pages - 1)}
                                    aria-label={`last page`}
                                    icon={<RiArrowRightDoubleLine />}
                                />
                            </HStack>
                        </Box>
                    </VStack>
                </Box>
            )}
        </OutletOrChildren>
    );
}

export default GamesGallery;
